import React, { createContext, ReactNode, useContext } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

export interface StorageContainer {
  type: string;
  name: string;
}

export interface StorageQueue {
  type: string;
  name: string;
}

export interface StorageFileshare {
  type: string;
  name: string;
  quotaGB: string;
}

interface StorageAccountChildren {
  storagecontainer?: StorageContainer[];
  storagequeue?: StorageQueue[];
  storagefileshare?: StorageFileshare[];
}

interface StorageAccount {
  name: string;
  entity?: string;
  owner?: string;
  domain?: string;
  system?: string;
  type?: string;
  accountKind?: string;
  environment?: string;
  children: StorageAccountChildren;
}

interface StorageAccountContextProps {
  data: StorageAccount;
  name: string;
  namespace: string;
  loading: boolean;
  error: Error | undefined;
}

interface StorageAccountProviderProps {
  children: ReactNode;
  name: string;
  namespace: string;
}

const StorageAccountContext = createContext<StorageAccountContextProps>({
  name: '',
  namespace: '',
  loading: false,
  error: undefined,
  data: {
    name: '',
    children: {},
  },
});

export const StorageAccountProvider = ({
  children,
  name,
  namespace,
}: StorageAccountProviderProps) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef) ?? { fetch };

  const getStorageAccount = async () => {
    const baseUrlData = await discoveryApi.getBaseUrl('storage-account');

    const urlData = `${baseUrlData}/entity/${namespace}/${name}`;

    const responseData = await fetchApi.fetch(urlData);
    if (!responseData.ok) {
      throw await ResponseError.fromResponse(responseData);
    }

    return await responseData.json();
  };

  const { value, loading, error } = useAsync(async () => {
    return await getStorageAccount();
  }, []);

  return (
    <StorageAccountContext.Provider
      value={{
        name,
        namespace,
        loading,
        error,
        data: value,
      }}
    >
      {children}
    </StorageAccountContext.Provider>
  );
};

export const useStorageAccountContext = () => useContext(StorageAccountContext);
